import React, { useEffect, useMemo, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import componentStyles from './parlayBetSlipModal.module.css';
import globalStyles from '../globals.module.css';
import sportsbookBetLinkOptions from '../../config/sportsbookBetLinkOptions';
import { generateBetLink } from '../../service/propsViewerService';
import AnalyticsService from '../../service/analyticsService';
import { EVENT_BETSLIP_GENERATED } from '../../utils/analyticsConstants';

const confidenceRank = {
    validated: 3,
    inferred: 2,
    unvalidated: 1
};

const normalizeBetType = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return '';
    if (normalized.includes('moneyline') || normalized === 'ml' || normalized === 'h2h') return 'moneyline';
    if (normalized.includes('spread') || normalized.includes('run line') || normalized.includes('puck line')) return 'spread';
    if (normalized.includes('total') || normalized === 'totals' || normalized.includes('over/under')) return 'total';
    if (normalized.includes('prop')) return 'prop';
    return normalized;
};

const stripOdds = (value) => String(value || '').replace(/\s*\([^)]*\)\s*$/g, '').trim();

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const parseLine = (value) => {
    const match = String(value || '').match(/(?:^|\s)([-+]?\d+(?:\.\d+)?)(?:\s|$)/);
    return match ? Number(match[1]) : null;
};

const parseDirection = (value) => {
    const match = String(value || '').match(/\b(over|under)\b/i);
    if (!match) return '';
    return match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
};

const inferSide = (bet, betType, recommendedBet) => {
    if (bet.side || bet.team) return bet.side || bet.team;
    if (betType === 'moneyline') return stripOdds(recommendedBet).replace(/\s+ML$/i, '').trim();
    if (betType === 'spread') {
        const line = bet.line ?? parseLine(recommendedBet);
        if (line === null || line === undefined) return '';
        return stripOdds(recommendedBet).replace(new RegExp(`\\s*${escapeRegExp(line)}\\s*$`), '').trim();
    }
    return '';
};

const getMarketType = (bet, betType) => {
    if (bet.marketType || bet.market_type) return bet.marketType || bet.market_type;
    if (betType === 'moneyline') return 'h2h';
    if (betType === 'spread') return 'spreads';
    if (betType === 'total') return 'totals';
    return '';
};

const normalizeParlayLeg = (bet) => {
    const source = bet.rawLeg || bet;
    const betType = normalizeBetType(source.betType || source.bet_type || bet.betType || bet.type);
    const recommendedBet = source.recommendedBet || source.recommended_bet || source.bet || source.label || bet.recommendedBet || bet.bet || bet.label || '';
    const line = source.line ?? bet.line ?? parseLine(recommendedBet);
    const direction = source.direction || bet.direction || parseDirection(recommendedBet);
    const side = inferSide({ ...bet, ...source }, betType, recommendedBet);

    const leg = {
        id: source.id || source.sourceBetId || source.source_bet_id || bet.id,
        betType,
        label: source.label || bet.label || recommendedBet,
        sport: source.sport || bet.sport,
        eventId: source.eventId || source.event_id || source.oddsApiEventId || source.odds_api_event_id || source.scoutingReportId || source.scouting_report_id || bet.eventId,
        marketType: getMarketType({ ...bet, ...source }, betType),
        recommendedBet,
        side,
        player: source.player || bet.player,
        direction,
        line,
        confidence: source.confidence || source.prop_bet_confidence || bet.confidence,
        explanation: source.explanation || bet.explanation
    };

    Object.keys(leg).forEach((key) => {
        if (leg[key] === '' || leg[key] === null || leg[key] === undefined) {
            delete leg[key];
        }
    });

    return leg;
};

const isValidBetLinkLeg = (leg) => {
    if (!leg || !leg.betType || !leg.sport || !leg.eventId || !leg.recommendedBet) return false;
    if (leg.betType === 'prop') return Boolean(leg.player && leg.marketType && leg.direction && leg.line !== undefined);
    if (leg.betType === 'moneyline') return Boolean(leg.side);
    if (leg.betType === 'spread') return Boolean(leg.side && leg.line !== undefined);
    if (leg.betType === 'total') return Boolean(leg.direction && leg.line !== undefined);
    return false;
};

const saveBetSlipId = (betSlipId, parlayId) => {
    if (!betSlipId || typeof window === 'undefined') return;
    try {
        const storageKey = 'parlayGenerator_betSlipIds';
        const existing = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
        const next = [
            {
                betSlipId,
                parlayId,
                createdAt: new Date().toISOString()
            },
            ...(Array.isArray(existing) ? existing : [])
        ].slice(0, 25);
        window.localStorage.setItem(storageKey, JSON.stringify(next));
    } catch (error) {
        // Non-critical; the betslip link itself remains usable.
    }
};

const ParlayBetSlipModal = ({ parlay, parlayIndex, onClose }) => {
    const [isClosing, setIsClosing] = useState(false);
    const [selectedBookmakerKey, setSelectedBookmakerKey] = useState('');
    const [selectedState, setSelectedState] = useState('');
    const [isBuilding, setIsBuilding] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const excludeDfs = sportsbookBetLinkOptions.defaultExcludeDfs !== false;
    const allowClosestLine = sportsbookBetLinkOptions.defaultAllowClosestLine !== false;

    const legs = useMemo(() => (parlay?.bets || []).map(normalizeParlayLeg), [parlay]);
    const invalidLegCount = legs.filter((leg) => !isValidBetLinkLeg(leg)).length;
    const sportsbookOptions = useMemo(() => (
        sportsbookBetLinkOptions.options
            .filter((option) => !excludeDfs || option.category !== 'dfs')
            .filter((option) => legs.length <= 1 || option.multiPickSupported)
            .sort((a, b) => {
                if (a.uiRecommended !== b.uiRecommended) return a.uiRecommended ? -1 : 1;
                if (a.multiPickSupported !== b.multiPickSupported) return a.multiPickSupported ? -1 : 1;
                const confidenceDelta = (confidenceRank[b.multiPickConfidence] || 0) - (confidenceRank[a.multiPickConfidence] || 0);
                if (confidenceDelta !== 0) return confidenceDelta;
                return a.displayName.localeCompare(b.displayName);
            })
    ), [excludeDfs, legs.length]);
    const selectedBookmaker = sportsbookOptions.find((option) => option.bookmakerKey === selectedBookmakerKey);

    useEffect(() => {
        if (sportsbookOptions.length > 0 && !sportsbookOptions.some((option) => option.bookmakerKey === selectedBookmakerKey)) {
            setSelectedBookmakerKey(sportsbookOptions[0].bookmakerKey);
        }
    }, [sportsbookOptions, selectedBookmakerKey]);

    const closeWithAnimation = () => {
        setIsClosing(true);
        setTimeout(onClose, 300);
    };

    const handleBuildSlip = async () => {
        setError('');
        setResult(null);

        if (!selectedBookmakerKey) {
            setError('Choose a sportsbook.');
            return;
        }

        if (selectedBookmaker?.requiresState && !selectedState.trim()) {
            setError('Enter a state for this sportsbook.');
            return;
        }

        if (invalidLegCount > 0) {
            setError('One or more parlay legs are missing link details.');
            return;
        }

        setIsBuilding(true);
        try {
            const response = await generateBetLink({
                source: 'parlay_generator',
                sourcePayload: {
                    parlayId: parlay?.id || `parlay-${parlayIndex + 1}`,
                    numLegs: legs.length
                },
                bookmakerKey: selectedBookmakerKey,
                state: selectedBookmaker?.requiresState ? selectedState.trim().toLowerCase() : '',
                allowClosestLine,
                excludeDfs,
                persist: true,
                legs
            });
            const payload = response?.data || {};
            if (payload.success === false) {
                setError(payload.error || 'Unable to generate a bet slip link.');
                return;
            }
            const results = payload.results || {};
            const parlayId = parlay?.id || `parlay-${parlayIndex + 1}`;
            saveBetSlipId(results.betSlipId, parlayId);
            setResult(results);
            AnalyticsService.getInstance().track(EVENT_BETSLIP_GENERATED, {
                source: 'parlay_generator',
                bookmakerKey: selectedBookmakerKey,
                legCount: legs.length,
                parlayId,
                parlayIndex: parlayIndex + 1,
                betSlipId: results.betSlipId || '',
                hasUrl: Boolean(results.url),
                fallbackCount: Array.isArray(results.fallbackUrls) ? results.fallbackUrls.length : 0,
                unresolvedCount: Array.isArray(results.unresolved) ? results.unresolved.length : 0,
                warningCount: Array.isArray(results.warnings) ? results.warnings.length : 0
            });
        } catch (requestError) {
            setError('Unable to generate a bet slip link.');
        } finally {
            setIsBuilding(false);
        }
    };

    return (
        <div className={`${componentStyles.modalOverlay} ${isClosing ? componentStyles.modalOverlayClosing : componentStyles.modalOverlayOpening}`} onClick={closeWithAnimation}>
            <div className={`${componentStyles.modalContent} ${isClosing ? componentStyles.modalContentClosing : componentStyles.modalContentOpening}`} onClick={(event) => event.stopPropagation()}>
                <div className={componentStyles.modalHeader}>
                    <h2 className={componentStyles.modalTitle}>Build Betslip</h2>
                    <button type="button" className={`${globalStyles.iconButton} ${globalStyles.squareButton} ${componentStyles.closeButton}`} onClick={closeWithAnimation} aria-label="Close betslip builder" title="Close">
                        <FaTimes />
                    </button>
                </div>

                <div className={componentStyles.modalBody}>
                    <div className={componentStyles.summaryRow}>
                        <span>Parlay #{parlayIndex + 1}</span>
                        <span>{legs.length} legs</span>
                    </div>

                    <div className={componentStyles.sportsbookOptions}>
                        {sportsbookOptions.map((option) => (
                            <button
                                key={option.bookmakerKey}
                                type="button"
                                className={`${globalStyles.controlButton} ${componentStyles.sportsbookOption} ${selectedBookmakerKey === option.bookmakerKey ? `${globalStyles.activeControl} ${componentStyles.sportsbookOptionActive}` : ''}`}
                                onClick={() => {
                                    setSelectedBookmakerKey(option.bookmakerKey);
                                    setError('');
                                    setResult(null);
                                }}
                            >
                                {option.displayName}
                            </button>
                        ))}
                    </div>

                    {selectedBookmaker?.requiresState && (
                        <input
                            className={componentStyles.stateInput}
                            type="text"
                            value={selectedState}
                            onChange={(event) => setSelectedState(event.target.value)}
                            placeholder="State, e.g. nj"
                            maxLength={2}
                        />
                    )}

                    {(error || result) && (
                        <div className={componentStyles.resultPanel}>
                            <div className={componentStyles.resultHeader}>
                                <span>Betslip response</span>
                                <span className={`${componentStyles.resultStatus} ${error ? componentStyles.statusError : componentStyles.statusReady}`}>
                                    {error ? 'Error' : 'Ready'}
                                </span>
                            </div>
                            <div className={componentStyles.resultBody}>
                                {error && <p className={componentStyles.errorText}>{error}</p>}
                                {Array.isArray(result?.warnings) && result.warnings.length > 0 && (
                                    <ul className={componentStyles.warningList}>
                                        {result.warnings.map((item, index) => (
                                            <li key={`${item}-${index}`}>{item}</li>
                                        ))}
                                    </ul>
                                )}
                                {result?.url && (
                                    <a className={`${globalStyles.primaryButton} ${componentStyles.openSlipLink}`} href={result.url} target="_blank" rel="noreferrer">
                                        Open betslip
                                    </a>
                                )}
                                {result?.message && <p className={componentStyles.resultMessage}>{result.message}</p>}
                                {Array.isArray(result?.unresolved) && result.unresolved.length > 0 && (
                                    <ul>
                                        {result.unresolved.map((item, index) => (
                                            <li key={`${item}-${index}`}>{item}</li>
                                        ))}
                                    </ul>
                                )}
                                {Array.isArray(result?.fallbackUrls) && result.fallbackUrls.length > 0 && (
                                    <div className={componentStyles.fallbackLinks}>
                                        {result.fallbackUrls.map((fallback, index) => {
                                            const url = typeof fallback === 'string' ? fallback : fallback.url;
                                            if (!url) return null;
                                            return (
                                                <a className={`${globalStyles.secondaryButton} ${globalStyles.compactButton}`} key={`${url}-${index}`} href={url} target="_blank" rel="noreferrer">
                                                    Open pick {index + 1}
                                                </a>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className={componentStyles.footerActions}>
                        <button type="button" className={globalStyles.secondaryButton} onClick={closeWithAnimation}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={globalStyles.primaryButton}
                            onClick={handleBuildSlip}
                            disabled={isBuilding || invalidLegCount > 0}
                        >
                            {isBuilding ? 'Generating...' : 'Generate Slip'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ParlayBetSlipModal;
